/**
 * Shared formatters and mappers for futures profile order pages
 * (open orders, order history, trade history, position history).
 */

export function dec(v) {
  if (v == null) return null;
  if (typeof v === "object" && v.$numberDecimal != null) return v.$numberDecimal;
  return v;
}

export function decNum(v) {
  const n = parseFloat(dec(v));
  return Number.isFinite(n) ? n : NaN;
}

/** Dropdown value (`btcusdt`) → API symbol (`BTCUSDT-PERP`). */
export function toFuturesApiSymbol(contract) {
  if (!contract || contract === "all") return undefined;
  const code = String(contract).replace(/[/_]/g, "").toUpperCase();
  return /-PERP$/.test(code) ? code : `${code}-PERP`;
}

export function formatFuturesSymbol(raw) {
  if (!raw) return "—";
  const s = String(raw).toUpperCase().replace(/-PERP(ETUAL)?$/i, "");
  if (s.includes("/")) return s;
  for (const q of ["USDT", "USDC", "BUSD"]) {
    if (s.endsWith(q) && s.length > q.length) return `${s.slice(0, -q.length)}/${q}`;
  }
  return s;
}

export function baseFromFuturesSymbol(sym, fallback = "BTC") {
  const s = String(sym ?? "").toUpperCase().replace(/-PERP(ETUAL)?$/i, "");
  for (const q of ["USDT", "USDC", "BUSD"]) {
    if (s.endsWith(q) && s.length > q.length) return s.slice(0, -q.length);
  }
  const m = s.match(/^([A-Z0-9]+)USDT/i);
  return m ? m[1] : fallback;
}

export function formatOrderTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function formatQty(v, dp = 8) {
  const n = decNum(v);
  if (!Number.isFinite(n)) return "—";
  const s = n.toFixed(dp).replace(/\.?0+$/, "");
  return s || "0";
}

export function formatPrice(v) {
  const n = decNum(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-US", { maximumFractionDigits: 8 });
}

export function formatFee(v, asset) {
  const n = decNum(v);
  if (!Number.isFinite(n)) return "—";
  const body = n.toFixed(8).replace(/\.?0+$/, "") || "0";
  return asset ? `${body} ${asset}` : body;
}

export function formatPnl(v, asset) {
  const n = decNum(v);
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n).toFixed(8).replace(/\.?0+$/, "");
  const signed = n > 0 ? `+${abs}` : n < 0 ? `-${abs}` : abs;
  return asset ? `${signed} ${asset}` : signed;
}

export function extractFuturesOrders(res) {
  if (!res || res.success === false) return [];
  if (Array.isArray(res?.data?.orders)) return res.data.orders;
  if (Array.isArray(res?.orders)) return res.orders;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

export function extractFuturesTrades(res) {
  if (!res || res.success === false) return [];
  if (Array.isArray(res?.data?.trades)) return res.data.trades;
  if (Array.isArray(res?.trades)) return res.trades;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

export function extractFuturesPositions(res) {
  if (!res || res.success === false) return [];
  if (Array.isArray(res?.data?.positions)) return res.data.positions;
  if (Array.isArray(res?.positions)) return res.positions;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

export function futuresOrderType(item) {
  return String(item?.order_type ?? item?.type ?? "").toUpperCase();
}

export function futuresTriggerPrice(item) {
  return decNum(item?.trigger_price ?? item?.triggerPrice ?? item?.stop_price ?? item?.stopPrice);
}

export function futuresLimitPrice(item) {
  return decNum(item?.price);
}

export function futuresOrderTypeLabel(item) {
  const t = futuresOrderType(item);
  const labels = {
    TAKE_PROFIT_MARKET: "TP Market",
    TAKE_PROFIT_LIMIT: "TP Limit",
    STOP_MARKET: "Stop Market",
    STOP_LIMIT: "Stop Limit",
    CONDITIONAL: "Conditional",
    MARKET: "Market",
    LIMIT: "Limit",
  };
  return labels[t] || t.replace(/_/g, " ");
}

export function futuresOrderStatusLabel(item) {
  const s = String(item?.status ?? "").toUpperCase();
  if (s === "PENDING_TRIGGER") return "Pending Trigger";
  return item?.status ?? "—";
}

export function tpSlText(item) {
  const tp = decNum(item?.take_profit);
  const sl = decNum(item?.stop_loss);
  if (!Number.isFinite(tp) && !Number.isFinite(sl)) return null;
  const parts = [];
  if (Number.isFinite(tp)) parts.push(`TP ${formatPrice(tp)}`);
  if (Number.isFinite(sl)) parts.push(`SL ${formatPrice(sl)}`);
  return parts.join(" · ");
}

export function futuresOrderPriceDisplay(item) {
  const t = futuresOrderType(item);
  const trigger = futuresTriggerPrice(item);
  const limit = futuresLimitPrice(item);

  if (t === "MARKET") return "Market";
  if (t === "STOP_MARKET" || t === "TAKE_PROFIT_MARKET") {
    return Number.isFinite(trigger) ? formatPrice(trigger) : "Market";
  }
  if (t === "STOP_LIMIT" || t === "TAKE_PROFIT_LIMIT" || t === "LIMIT") {
    if (Number.isFinite(limit) && limit > 0) return formatPrice(limit);
  }
  if (t === "CONDITIONAL") {
    const exec = decNum(item?.order_price);
    if (Number.isFinite(exec) && exec > 0) return formatPrice(exec);
  }
  if (Number.isFinite(trigger) && trigger > 0) return formatPrice(trigger);
  return "—";
}

export function futuresOrderPriceSubtext(item) {
  const attached = tpSlText(item);
  if (attached) return attached;

  const t = futuresOrderType(item);
  const trigger = futuresTriggerPrice(item);
  const limit = futuresLimitPrice(item);

  if ((t === "STOP_LIMIT" || t === "TAKE_PROFIT_LIMIT") && Number.isFinite(trigger)) {
    return `Trigger ${formatPrice(trigger)}`;
  }
  if (t === "CONDITIONAL" && Number.isFinite(trigger)) {
    if (Number.isFinite(limit) && limit > 0) {
      return `Trigger ${formatPrice(trigger)} · Limit ${formatPrice(limit)}`;
    }
    return `Trigger ${formatPrice(trigger)}`;
  }
  if ((t === "STOP_MARKET" || t === "TAKE_PROFIT_MARKET") && Number.isFinite(trigger)) {
    return t === "TAKE_PROFIT_MARKET" ? "TP trigger" : "SL trigger";
  }
  return null;
}

export function orderAvgFill(item) {
  const a = decNum(item?.average_execution_price);
  if (Number.isFinite(a) && a > 0) return formatPrice(a);
  const filled = decNum(item?.filled_quantity);
  if (Number.isFinite(filled) && filled > 0) {
    const fallback = futuresTriggerPrice(item);
    const limit = futuresLimitPrice(item);
    const px = Number.isFinite(limit) && limit > 0 ? limit : fallback;
    if (Number.isFinite(px) && px > 0) return formatPrice(px);
  }
  return "—";
}

export function orderFeeDisplay(item) {
  const fee = decNum(item?.total_fees_paid ?? item?.total_fees);
  if (!Number.isFinite(fee)) return "—";
  return `${fee} ${item?.fee_asset ?? "USDT"}`;
}

export function statusLabel(status) {
  const s = String(status || "").toUpperCase();
  const labels = {
    FILLED: "Filled",
    CANCELLED: "Cancelled",
    CANCELED: "Cancelled",
    REJECTED: "Rejected",
    EXPIRED: "Expired",
    NEW: "New",
    PARTIALLY_FILLED: "Partially Filled",
    PENDING_TRIGGER: "Pending Trigger",
  };
  return labels[s] || (s ? s.charAt(0) + s.slice(1).toLowerCase() : "—");
}

export function fillRemark(filledQty, totalQty) {
  const filled = decNum(filledQty);
  const total = decNum(totalQty);
  if (!Number.isFinite(filled) || !Number.isFinite(total) || total <= 0) return "—";
  if (filled <= 0) return "Unfilled";
  return filled >= total ? "Fully Filled" : "Partially Filled";
}

export function formatCloseReason(reason, status) {
  const r = String(reason ?? status ?? "").toUpperCase();
  if (r === "USER") return "Closed manually";
  if (r === "LIQUIDATED" || r === "LIQUIDATION") return "Liquidated";
  if (r === "ADL") return "ADL";
  if (r === "CLOSED") return "Closed";
  if (r === "EXPIRED") return "Expired";
  return reason || status || "—";
}

export function isLiquidatedPosition(pos) {
  const reason = String(pos?.close_reason || "").toUpperCase();
  const status = String(pos?.status || "").toUpperCase();
  return reason === "LIQUIDATION" || reason === "LIQUIDATED" || status === "LIQUIDATED";
}

export function formatLiqFee(pos, asset) {
  const n = decNum(pos?.liq_fee);
  if (!isLiquidatedPosition(pos) || !Number.isFinite(n) || n === 0) return null;
  const feeAsset = pos?.liq_fee_asset || asset || "";
  return { value: parseFloat(n.toFixed(4)).toString(), asset: feeAsset };
}

export function tradeUserView(ex, userId) {
  const uid = userId || (typeof localStorage !== "undefined" ? localStorage.getItem("userId") : null);
  const takerSide = String(ex?.side ?? ex?.taker_side ?? "").toUpperCase();
  const flip = (s) => (s === "BUY" ? "SELL" : s === "SELL" ? "BUY" : s);
  const isTaker = uid && ex?.taker_user_id && String(ex.taker_user_id) === String(uid);
  const isMaker = uid && ex?.maker_user_id && String(ex.maker_user_id) === String(uid);
  if (isMaker && !isTaker) {
    return {
      side: flip(takerSide) || "—",
      role: "Maker",
      orderId: ex.maker_order_id || "—",
      fee: ex.maker_fee,
      pnl: ex.maker_realized_pnl,
    };
  }
  return {
    side: takerSide || "—",
    role: isTaker ? "Taker" : (ex?.taker_role ? "Taker" : "—"),
    orderId: ex.taker_order_id || ex.maker_order_id || "—",
    fee: ex.taker_fee,
    pnl: ex.taker_realized_pnl,
  };
}

export function computeClosedFuturesPosition(pos) {
  const entry = decNum(pos?.average_entry_price ?? pos?.entry_price);
  const exit = decNum(pos?.average_exit_price ?? pos?.close_price);
  let qty = decNum(pos?.closed_quantity ?? pos?.quantity);
  if ((!Number.isFinite(qty) || qty <= 0) && Number.isFinite(entry) && entry > 0) {
    const initMargin = decNum(pos?.initial_margin ?? pos?.isolated_margin_allocated ?? pos?.closed_initial_margin);
    const lev = Number(pos?.leverage);
    if (Number.isFinite(initMargin) && initMargin > 0 && Number.isFinite(lev) && lev > 0) {
      qty = (initMargin * lev) / entry;
    }
  }
  const marginAsset = pos?.margin_asset || pos?.pnl_asset || "USDT";
  const assetBase = baseFromFuturesSymbol(pos?.symbol);
  const rpnlRaw = dec(pos?.realized_pnl);
  const rpnl = decNum(pos?.realized_pnl);
  return {
    entry,
    exit,
    qty,
    rpnlRaw,
    rpnl,
    marginAsset,
    assetBase,
    closeLabel: formatCloseReason(pos?.close_reason, pos?.status),
    liqFee: formatLiqFee(pos, marginAsset),
  };
}

export function mapFuturesOpenOrderRow(r, idx) {
  const side = String(r.side ?? "").toUpperCase();
  const filled = decNum(r.filled_quantity);
  const total = decNum(r.quantity);
  const remaining = decNum(r.remaining_quantity);
  const unfilled = Number.isFinite(remaining)
    ? remaining
    : (Number.isFinite(total) && Number.isFinite(filled) ? Math.max(total - filled, 0) : NaN);
  const priceDisplay = futuresOrderPriceDisplay(r);
  const priceSubtext = futuresOrderPriceSubtext(r);
  const base = baseFromFuturesSymbol(r.symbol);
  return {
    id: r._id ?? r.order_id ?? r.client_order_id ?? idx,
    raw: r,
    contract: formatFuturesSymbol(r.symbol),
    rawSymbol: r.symbol,
    orderId: r.client_order_id || r._id || r.order_id || "—",
    creationTime: formatOrderTime(r.created_at),
    createdAt: r.created_at,
    side: side === "BUY" ? "Buy" : side === "SELL" ? "Sell" : side || "—",
    sideRaw: side,
    orderType: futuresOrderType(r),
    orderTypeLabel: futuresOrderTypeLabel(r),
    price: priceDisplay,
    priceSubtext,
    size: formatQty(r.quantity),
    unfilled: formatQty(unfilled),
    fillPrice: orderAvgFill(r),
    source: r.source || r.order_source || "—",
    remarks: r.reduce_only ? "Reduce Only" : fillRemark(r.filled_quantity, r.quantity),
    status: futuresOrderStatusLabel(r),
    statusRaw: String(r.status ?? "").toUpperCase(),
    leverage: r.leverage,
    marginMode: r.margin_mode,
    tif: r.time_in_force ?? "GTC",
    baseAsset: base,
  };
}

export function mapFuturesOrderHistoryRow(r, idx) {
  const side = String(r.side ?? "").toUpperCase();
  const filled = formatQty(r.filled_quantity);
  const total = formatQty(r.quantity);
  return {
    id: r._id ?? r.order_id ?? r.client_order_id ?? idx,
    raw: r,
    contract: formatFuturesSymbol(r.symbol),
    rawSymbol: r.symbol,
    orderType: futuresOrderType(r),
    orderTypeLabel: futuresOrderTypeLabel(r),
    orderId: r.client_order_id || r._id || r.order_id || "—",
    creationTime: formatOrderTime(r.created_at),
    createdAt: r.created_at,
    endTime: formatOrderTime(r.filled_at ?? r.updated_at),
    side: side === "BUY" ? "Buy" : side === "SELL" ? "Sell" : side || "—",
    price: futuresOrderPriceDisplay(r),
    priceSubtext: futuresOrderPriceSubtext(r),
    fillPrice: orderAvgFill(r),
    size: filled === total ? total : `${filled} / ${total}`,
    fee: orderFeeDisplay(r),
    remarks: fillRemark(r.filled_quantity, r.quantity),
    closeReason: r.cancelled_reason || r.rejection_reason || "—",
    status: statusLabel(r.status),
    isFilled: String(r.status ?? "").toUpperCase() === "FILLED",
    leverage: r.leverage != null ? `${r.leverage}x · ${r.margin_mode ?? ""}`.trim() : "—",
    tif: r.time_in_force ?? "GTC",
    reduceOnly: r.reduce_only ? "Yes" : "No",
  };
}

export function mapFuturesTradeRow(r, idx, userId) {
  const view = tradeUserView(r, userId);
  const side = view.side === "BUY" ? "Buy" : view.side === "SELL" ? "Sell" : view.side;
  const pnlNum = decNum(view.pnl);
  const totalNum = decNum(r.execution_notional_usd);
  const total = Number.isFinite(totalNum)
    ? totalNum
    : (decNum(r.execution_price ?? r.price) * decNum(r.execution_quantity ?? r.quantity)) || NaN;
  return {
    id: r._id ?? r.execution_id ?? r.trade_id ?? idx,
    contract: formatFuturesSymbol(r.symbol),
    rawSymbol: r.symbol,
    tradeTime: formatOrderTime(r.executed_at ?? r.created_at ?? r.timestamp),
    executedAt: r.executed_at ?? null,
    executedAtDisplay: formatOrderTime(r.executed_at),
    orderId: view.orderId,
    size: formatQty(r.execution_quantity ?? r.quantity),
    fillPrice: formatPrice(r.execution_price ?? r.price),
    fee: formatFee(view.fee, r.fee_asset || r.settlement_asset),
    pnl: formatPnl(view.pnl, r.pnl_asset || r.settlement_asset),
    pnlNum,
    role: view.role,
    side,
    total: Number.isFinite(total) ? total : null,
  };
}

export function mapFuturesPositionHistoryRow(r, idx) {
  const c = computeClosedFuturesPosition(r);
  const side = String(r.side ?? "—").toUpperCase();
  const isLong = side === "LONG" || side === "BUY";
  const rpnlStr = c.rpnlRaw != null && c.rpnlRaw !== "" ? `${c.rpnlRaw} ${c.marginAsset}` : "—";
  return {
    id: r._id ?? r.id ?? idx,
    raw: r,
    contract: formatFuturesSymbol(r.symbol),
    rawSymbol: r.symbol,
    side,
    sideLabel: isLong ? "Long" : side === "SHORT" || side === "SELL" ? "Short" : side,
    isLong,
    status: c.closeLabel,
    statusRaw: String(r.status ?? "—"),
    entryTime: formatOrderTime(r.opened_at ?? r.created_at),
    openedAt: r.opened_at ?? r.created_at,
    closedAt: r.closed_at,
    closedTime: formatOrderTime(r.closed_at),
    entryPrice: formatPrice(c.entry),
    avgExitPrice: formatPrice(c.exit),
    cumulativeClosed: formatQty(c.qty),
    peakPosition: formatQty(c.qty),
    assetBase: c.assetBase,
    realizedReturn: rpnlStr,
    realizedPct: "—",
    pnlNum: c.rpnl,
    liqFee: c.liqFee,
    leverage: r.leverage,
    marginMode: r.margin_mode,
  };
}

export function matchesFuturesOrderType(rowOrderType, filterType) {
  if (!filterType || filterType === "all") return true;
  const row = String(rowOrderType || "").toUpperCase();
  const f = String(filterType).toUpperCase();
  if (row === f) return true;
  if (f === "CONDITIONAL") {
    return ["STOP_MARKET", "STOP_LIMIT", "TAKE_PROFIT_MARKET", "TAKE_PROFIT_LIMIT", "CONDITIONAL"].includes(row);
  }
  return false;
}
